<template>
  <div class="page-container">
    <div class="page-content">
      <scan-reader ref="reader" :on-read="(data) => validCode(data)"/>

      <button
        v-if="stationMode !== 'lead' && filterOptions && filterOptions.length > 0"
        v-show="!onReady"
        type="button"
        class="remember-tag-btn"
        :class="{ 'remember-tag-btn-on': rememberTag }"
        :aria-pressed="rememberTag ? 'true' : 'false'"
        :aria-label="rememberTag ? 'Esquecer tag entre sessões' : 'Lembrar tag entre sessões'"
        :title="rememberTag ? 'Tag fica memorizada — clique para esquecer' : 'Memorizar tag para próximas sessões'"
        @click="toggleRememberTag"
      >
        <i class="fas fa-thumbtack"></i>
      </button>

      <scan-filter ref="filter" :filter="filterTags" v-show="!onReady" @filter="(data) => setFilter(data)" @close="handleFilterClose"/>
      <scan-result ref="result" :filter="filterTags" @need-filter="openFilter"/>
    </div>
    <div class="page-footer">
      <router-link class="text" :to="{name: routeHomeName}">
        <i class='icon bx bx-home'></i>
        Home
      </router-link>
    </div>
  </div>
</template>

<script>
import { CHECK_IN_PAGE_INDEX } from '@/modules/check-in/routes'
import ScanReader from '@/modules/check-in/pages/scan/scan-reader'
import ScanFilter from '@/modules/check-in/pages/scan/scan-filter'
import ScanResult from '@/modules/check-in/pages/scan/scan-result'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { CHECK_IN_MODULE } from '@/modules/check-in'

// Chaves do localStorage para a feature "lembrar tag". Separadas em duas:
// uma para a preferência (toggle on/off) e outra para os IDs lembrados.
// Não reutilizamos a chave `filterTags` antiga — aquela aplicava tags
// silenciosamente, o que era o bug; aqui o toggle é explícito.
const REMEMBER_TAG_KEY = 'checkin_remember_tag_enabled'
const REMEMBERED_TAG_IDS_KEY = 'checkin_remembered_tag_ids'

export default {
  name: 'scan',
  components: { ScanResult, ScanFilter, ScanReader },
  data: () => ({
    routeHomeName: CHECK_IN_PAGE_INDEX,
    filterTags: [],
    onReady: false,
    awaitingTagResolve: null,
    settingReady: null,
    rememberTag: false
  }),
  computed: {
    ...mapState(CHECK_IN_MODULE, {
      filterOptions: state => state.tags.list
    }),
    ...mapGetters(CHECK_IN_MODULE, ['stationMode', 'brindeTag']),
    ...mapGetters('event', ['requireTagOnScan']),
    needsTagSelection () {
      // Lead não usa tag. Brinde SEMPRE exige a tag (é o brinde da estação).
      // Check-in segue a config require_tag_on_scan.
      if (this.stationMode === 'lead') return false
      if (this.stationMode === 'brinde') return this.filterTags.length === 0
      return this.requireTagOnScan && this.filterTags.length === 0
    }
  },
  watch: {
    // No modo Brinde, as tags escolhidas no scan-filter (multi) SÃO os brindes da
    // estação (um brinde = uma tag). Sincroniza TODAS no store pra o sidebar
    // (checklist) e o clique manual usarem.
    filterTags () { this.syncBrindeTags() },
    stationMode () { this.syncBrindeTags() },
    filterOptions () { this.syncBrindeTags() }
  },
  methods: {
    ...mapActions(CHECK_IN_MODULE, ['loadTagsList']),
    ...mapMutations(CHECK_IN_MODULE, ['setBrindeTags']),
    syncBrindeTags () {
      if (this.stationMode !== 'brinde') return
      const opts = this.filterOptions || []
      const tags = (this.filterTags || []).map(id => {
        const t = opts.find(o => o.id === id)
        return t ? { id: t.id, name: t.text } : { id, name: '' }
      })
      this.setBrindeTags(tags)
    },

    setFilter (filter) {
      this.filterTags = filter
      // Se "Lembrar tag" estiver ativo, persiste em localStorage. Se não,
      // a tag vive só em memória (sai/volta = começa limpo).
      this.persistTagIfRemembering(filter)
      if (this.awaitingTagResolve) {
        this.awaitingTagResolve()
        this.awaitingTagResolve = null
      }
    },

    openFilter () {
      this.$refs.filter.showSelectFilter()
    },

    handleFilterClose () {
      if (this.awaitingTagResolve) {
        this.awaitingTagResolve()
        this.awaitingTagResolve = null
      }
    },

    toggleRememberTag () {
      this.rememberTag = !this.rememberTag
      if (this.rememberTag) {
        localStorage.setItem(REMEMBER_TAG_KEY, '1')
        // Já tem tag aplicada agora? Persiste imediatamente para que a
        // próxima sessão restaure sem o usuário precisar reaplicar.
        this.persistTagIfRemembering(this.filterTags)
      } else {
        localStorage.removeItem(REMEMBER_TAG_KEY)
        localStorage.removeItem(REMEMBERED_TAG_IDS_KEY)
      }
    },

    persistTagIfRemembering (filter) {
      if (!this.rememberTag) return
      if (Array.isArray(filter) && filter.length > 0) {
        localStorage.setItem(REMEMBERED_TAG_IDS_KEY, JSON.stringify(filter))
      } else {
        // Tag foi limpa pelo usuário (ex.: LIMPAR + APLICAR) — reflete
        // isso no que está memorizado, senão na próxima sessão restaura
        // uma tag que já foi explicitamente removida.
        localStorage.removeItem(REMEMBERED_TAG_IDS_KEY)
      }
    },

    restoreRememberedTag () {
      this.rememberTag = localStorage.getItem(REMEMBER_TAG_KEY) === '1'
      if (!this.rememberTag) return false
      const saved = localStorage.getItem(REMEMBERED_TAG_IDS_KEY)
      if (!saved) return false
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.filterTags = parsed
          return true
        }
      } catch (e) { /* JSON inválido — ignora e segue como se não tivesse */ }
      return false
    },

    async validCode (code) {
      // Garante que `requireTagOnScan` esteja carregado do backend antes de
      // decidir se forçamos a seleção de tag. Sem isso, uma leitura disparada
      // logo após abrir a aba (antes do GetSetting concluir) passaria direto.
      if (this.settingReady) {
        await this.settingReady
      }
      // Gate de seleção de tag (mode-aware): check-in conforme a config,
      // brinde SEMPRE (a tag é o brinde), lead nunca.
      if (this.needsTagSelection) {
        // Bloqueia até o usuário selecionar tag ou fechar o modal.
        // Descartamos esta leitura — após o cooldown do scan-reader, o
        // próximo frame com o MESMO QR dispara uma nova leitura: se uma
        // tag foi selecionada, segue o caminho normal; se o modal foi
        // fechado sem seleção, o modal reabre.
        await new Promise(resolve => {
          this.awaitingTagResolve = resolve
          this.$refs.filter.showSelectFilter()
        })
        return
      }
      this.onReady = true
      await this.$refs.result.validateCode(code)
      this.onReady = false
    }
  },
  async mounted () {
    this.settingReady = this.$store.dispatch('event/GetSetting')
    // Garante a lista de tags (usada pelo seletor da tag do brinde neste modo).
    this.loadTagsList()

    // Cleanup pontual: a chave antiga `filterTags` aplicava tags silenciosamente
    // (era o bug). A persistência agora é controlada pelo botão "Lembrar tag".
    localStorage.removeItem('filterTags')

    // Restaura a tag memorizada SE o toggle "Lembrar tag" estava ativado.
    // Caso contrário, sai/volta começa sempre limpo.
    const restored = this.restoreRememberedTag()

    // Espera o setting carregar do backend antes de decidir se abre o modal de tag.
    // Sem o await, `requireTagOnScan` ainda estaria undefined no store e
    // `needsTagSelection` retornaria false, deixando passar leituras sem tag.
    await this.settingReady

    // Abre o modal apenas se:
    //  - Nada foi restaurado (precisa que o usuário escolha agora), OU
    //  - É exigido tag e ainda está vazio (`needsTagSelection`).
    // Quando o "Lembrar tag" restaurou com sucesso, o modal NÃO abre — a
    // intenção do toggle é justamente pular essa etapa em sessões futuras.
    // Lead não seleciona tag. Check-in e Brinde abrem o seletor se preciso
    // (brinde exige a tag; check-in conforme config), a menos que já restaurado.
    this.syncBrindeTags()
    if (this.stationMode !== 'lead' && this.$refs.filter && (!restored || this.needsTagSelection)) {
      this.$refs.filter.showSelectFilter()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "src/design/variables";

.page-container {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: #E5E5E5;
  display: flex;
  flex-direction: column;
}

.remember-tag-btn {
  position: absolute;
  @media (min-width: map-get($grid-breakpoints, "md")) {
    top: 63px;
    left: 68px;
  }
  top: 23px;
  left: 21px;
  background-color: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: 0;
  border-radius: 7px;
  display: grid;
  place-items: center;
  font-size: 22px;
  padding: 16px 18px;
  box-shadow: 0 4px 4px 0 #00000040;
  z-index: 5;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  i {
    transition: transform 0.2s ease;
  }

  &.remember-tag-btn-on {
    background-color: #FFC107;
    color: #292F42;

    i {
      transform: rotate(-30deg);
    }
  }
}

.page-content {
  flex: 1;
  padding: 0;
  position: relative;
  /* Fundo escuro atrás do vídeo: evita "linha branca/cinza" numa eventual folga
     de subpixel entre a câmera e o rodapé. Sem overflow:hidden — ele cortaria a
     lista do multiselect de tags (que abre pra baixo). */
  background-color: #292F42;
}

.page-footer {
  background-color: #292F42;
  display: grid;
  place-items: center;
  min-height: 64px;
  /* Acima da câmera (absolute): garante que o botão Home sempre receba o clique. */
  position: relative;
  z-index: 10;

  .text {
    color: #FFFFFF;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 14px;
    font-weight: 300;

    .icon {
      font-size: 32px;
    }
  }
}
</style>
